import React, { useMemo } from 'react';
import { Grid, Paper, Typography, Box, Stack, Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { feedbackHeroTokens } from './feedbackHeroTokens';

const ORIGEM_DEFS = [
  { key: 'formulario', label: 'Formulário', aliases: ['feedback_form', 'formulario', 'formulário'] },
  { key: 'atendabot', label: 'Atendabot', aliases: ['atendabot'] },
  { key: 'reservo', label: 'Reservo', aliases: ['reservo'] },
  { key: 'ifood', label: 'iFood', aliases: ['ifood'] },
  { key: 'google', label: 'Google', aliases: ['google'] },
];

const VALID_KEYS = new Set(ORIGEM_DEFS.map((d) => d.key));

const normalize = (value) => String(value ?? '').trim().toLowerCase();

const getOrigemKey = (raw) => {
  const norm = normalize(raw);
  if (!norm) return null;
  const match = ORIGEM_DEFS.find((def) => def.aliases.some((a) => normalize(a) === norm));
  return match ? match.key : null;
};

const getToneByMedia = (media, colors) => {
  if (media === null) return colors.neu;
  if (media >= 4) return colors.pos;
  if (media >= 3) return colors.neu;
  return colors.neg;
};

const OrigemRow = ({ row, total, isLeader, colors, fonts }) => {
  const isEmpty = row.total === 0;
  const pct = total > 0 ? Math.round((row.total / total) * 100) : 0;
  const barPct = total > 0 ? (row.total / total) * 100 : 0;
  const tone = getToneByMedia(row.media, colors);

  const mutedColor = 'rgba(15, 23, 42, 0.32)';
  const mutedSubColor = 'rgba(15, 23, 42, 0.22)';

  const tooltipTitle = isEmpty ? (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{row.label}</Typography>
      <Typography sx={{ fontSize: '0.75rem', opacity: 0.85 }}>
        Nenhum feedback recebido por este canal no período.
      </Typography>
    </Box>
  ) : (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
        {row.label} {isLeader ? '· canal líder' : ''}
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', opacity: 0.85 }}>
        {row.total} {row.total === 1 ? 'feedback' : 'feedbacks'} ({pct}% do total)
      </Typography>
      {row.media !== null && (
        <Typography sx={{ fontSize: '0.75rem', opacity: 0.85 }}>
          Nota média: {row.media.toFixed(2)} ★
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
    <Box
      sx={{
        position: 'relative',
        p: 1.25,
        borderRadius: 2,
        background: isLeader && !isEmpty ? `${tone.bg}55` : 'transparent',
        border: isLeader && !isEmpty ? `1px solid ${tone.bg}` : '1px solid transparent',
        opacity: isEmpty ? 0.55 : 1,
        cursor: isEmpty ? 'default' : 'pointer',
        transition: 'background .25s ease, opacity .25s ease, transform .25s ease, box-shadow .25s ease',
        '&:hover': {
          background: isEmpty ? 'transparent' : `${tone.bg}AA`,
          transform: isEmpty ? 'none' : 'translateX(2px)',
          boxShadow: isEmpty ? 'none' : `0 4px 12px ${tone.accent}1F`,
          opacity: isEmpty ? 0.7 : 1,
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          {isLeader && !isEmpty && (
            <EmojiEventsOutlinedIcon sx={{ fontSize: 16, color: tone.accent }} />
          )}
          <Typography
            sx={{
              fontFamily: fonts.body,
              fontWeight: isLeader && !isEmpty ? 700 : 600,
              fontSize: '0.9rem',
              color: isEmpty ? mutedColor : colors.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.label}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.25} alignItems="center">
          {row.media !== null ? (
            <Stack direction="row" spacing={0.25} alignItems="center">
              <StarIcon sx={{ fontSize: 13, color: tone.accent }} />
              <Typography
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  color: tone.fg,
                }}
              >
                {row.media.toFixed(1)}
              </Typography>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.25} alignItems="center">
              <StarBorderIcon sx={{ fontSize: 13, color: mutedSubColor }} />
              <Typography
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  color: mutedSubColor,
                }}
              >
                —
              </Typography>
            </Stack>
          )}
          <Typography
            sx={{
              fontFamily: fonts.display,
              fontWeight: 800,
              fontSize: '0.95rem',
              color: isEmpty ? mutedColor : colors.text,
              minWidth: 22,
              textAlign: 'right',
            }}
          >
            {row.total}
          </Typography>
          <Typography
            sx={{
              fontFamily: fonts.body,
              fontWeight: 600,
              fontSize: '0.75rem',
              color: isEmpty ? mutedSubColor : colors.subtext,
              minWidth: 40,
              textAlign: 'right',
            }}
          >
            {pct}%
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          width: '100%',
          height: 6,
          borderRadius: 6,
          background: colors.surfaceAlt,
          border: `1px solid ${colors.softBorder}`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${barPct}%`,
            height: '100%',
            background: isEmpty
              ? 'transparent'
              : `linear-gradient(90deg, ${tone.accent} 0%, ${tone.accent}AA 100%)`,
            transition: 'width 0.7s cubic-bezier(.2,.8,.2,1)',
          }}
        />
      </Box>
    </Box>
    </Tooltip>
  );
};

const FeedbackOrigemCard = ({ feedbacks = [] }) => {
  const { fonts, colors } = feedbackHeroTokens;

  const { rows, totalFeedbacks } = useMemo(() => {
    const map = new Map();
    ORIGEM_DEFS.forEach((def) => {
      map.set(def.key, { key: def.key, label: def.label, total: 0, somaNotas: 0, qtdNotas: 0 });
    });

    let validTotal = 0;
    feedbacks.forEach((fb) => {
      const key = getOrigemKey(fb.origem);
      if (!key || !VALID_KEYS.has(key)) return;
      const bucket = map.get(key);
      bucket.total += 1;
      validTotal += 1;
      const nota = Number(fb.nota);
      if (Number.isFinite(nota) && nota > 0) {
        bucket.somaNotas += nota;
        bucket.qtdNotas += 1;
      }
    });

    const allRows = Array.from(map.values()).map((b) => ({
      ...b,
      media: b.qtdNotas > 0 ? b.somaNotas / b.qtdNotas : null,
    }));

    const sorted = [...allRows].sort((a, b) => b.total - a.total);
    return { rows: sorted, totalFeedbacks: validTotal };
  }, [feedbacks]);

  return (
    <Grid item xs={12} md={6}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
        style={{ height: '100%' }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: { xs: 2, sm: 3 },
            background: colors.surface,
            border: `1px solid ${colors.softBorder}`,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" mb={0.5}>
            <HubOutlinedIcon sx={{ fontSize: 20, color: colors.text }} />
            <Typography
              sx={{
                fontFamily: fonts.display,
                fontWeight: 700,
                color: colors.text,
                fontSize: { xs: '1.05rem', sm: '1.15rem' },
              }}
            >
              Origem dos feedbacks
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            sx={{
              fontFamily: fonts.body,
              color: colors.subtext,
              display: 'block',
              mb: 2.5,
              fontSize: '0.82rem',
            }}
          >
            Volume e nota média por canal de atendimento.
          </Typography>

          <Stack spacing={1}>
            {rows.map((row, idx) => (
              <OrigemRow
                key={row.key}
                row={row}
                total={totalFeedbacks}
                isLeader={idx === 0 && row.total > 0}
                colors={colors}
                fonts={fonts}
              />
            ))}
          </Stack>

          {totalFeedbacks > 0 ? (
            <Typography
              variant="caption"
              sx={{
                fontFamily: fonts.body,
                color: colors.subtext,
                display: 'block',
                mt: 2,
                fontSize: '0.72rem',
                fontStyle: 'italic',
              }}
            >
              Base: {totalFeedbacks} {totalFeedbacks === 1 ? 'feedback' : 'feedbacks'} nos canais monitorados.
            </Typography>
          ) : (
            <Typography
              variant="caption"
              sx={{
                fontFamily: fonts.body,
                color: colors.subtext,
                display: 'block',
                mt: 2,
                fontSize: '0.72rem',
                fontStyle: 'italic',
              }}
            >
              Nenhum feedback recebido nos canais monitorados no período.
            </Typography>
          )}
        </Paper>
      </motion.div>
    </Grid>
  );
};

export default FeedbackOrigemCard;
